/**
 * The ONE rule for turning a product-search pick into a document line.
 *
 * The product search (stockService.searchVariantsForInvoice with
 * includeServices) returns two kinds of row: stock products and services. A
 * line's `product` slot points at products only. All six line grids used to
 * write `product: variant.id` for whatever was picked, so picking a service
 * put a service id in the product slot and the server refused the whole save
 * with 'Invalid pk' (invoice-line-service-saves-as-product, 2026-09-14).
 *
 * A picked service now fills the line exactly like "Create service" on the
 * same screen: no product link, the service description, its GL account.
 * Quantity, unit, price and currency stay the operator's.
 *
 * Used by: Outgoing / Proforma / Credit Note / Debit Note / Incoming /
 * Purchase Order Screen2. pickedSearchResult.everyGridUsesIt.test.js fails if
 * a grid writes a picked row's id into `product` any other way.
 */

export const isServiceResult = (result) => Boolean(result && result.is_service);

/** What the line's description (and the search box) should read. */
export const pickedResultLabel = (result) => {
  if (!result) return '';
  if (isServiceResult(result)) {
    return result.full_description || result.base_item_name || '';
  }
  return result.display_name
    ? result.display_name.split(' (Sale:')[0].split(' (Purchase:')[0]
    : result.full_description;
};

/**
 * The currency a pick brings to the line, or null when it brings none.
 * A service has no price, so it can never clash with the document's currency.
 */
export const pickedResultCurrency = (result) =>
  isServiceResult(result) ? null : (result.price_currency || 'USD');

/**
 * @param {object} line      the current line
 * @param {object} result    the picked search row
 * @param {object} opts
 * @param {'sale_price'|'purchase_price'} opts.priceField  which catalogue price the document uses
 * @param {Array<{value:any}>} [opts.units]                unit options, first is the fallback
 * @returns {object} the new line (the input is not mutated)
 */
export const applyPickedResultToLine = (line, result, { priceField, units = [] } = {}) => {
  const label = pickedResultLabel(result);

  if (isServiceResult(result)) {
    return {
      ...line,
      product: null,
      item_description: label,
      gl_account: result.gl_account_id || line.gl_account || null,
    };
  }

  if (priceField !== 'sale_price' && priceField !== 'purchase_price') {
    throw new Error(`applyPickedResultToLine: priceField must be sale_price or purchase_price, got ${priceField}`);
  }

  return {
    ...line,
    product: result.id,
    item_description: label,
    quantity: result.quantity || 0,
    unit: result.unit_id || (units.length > 0 ? units[0].value : null),
    unit_price: result[priceField] || 0,
    // Catalogue price has just been (re)applied: the first manual edit
    // re-prompts the override warning.
    unit_price_overridden: false,
    currency: pickedResultCurrency(result),
  };
};
